// ---- Floating combat damage / miss numbers ----
// Brief popups that rise over a target's head and fade out:
//   • red "12"  — HP-drop edge in net.ts (same edge as the flinch), so the
//                 number always matches what the server applied
//   • muted "Miss" — projectile arrival when the server rolled a band miss
//                     (see projectile.ts; near-broadcast like the arrow itself)
// World-anchored: each float freezes the head point at spawn, then rises in
// *screen* space so orbit/distance don't skew the pop. Pool the DOM nodes.

import * as THREE from "three";

/** Total lifetime of a float (seconds). */
const LIFE = 0.9;
/** How far (px) the number rises over its life. */
const RISE = 52;
/** Screen-x offset from center — successive hits alternate left / right. */
const SIDE = 22;

type Floater = {
  el: HTMLDivElement;
  wx: number; wy: number; wz: number;
  age: number;
  jx: number;
};

let container: HTMLElement | null = null;
const floats: Floater[] = [];
const pool: HTMLDivElement[] = [];
const _v = new THREE.Vector3();
/** Flip-flop so consecutive pops don't stack: −1 = left, +1 = right. */
let side = 1;

export function initDmgFloats(): void {
  container = document.getElementById("dmgfloats");
}

function takeEl(kind: "hit" | "miss"): HTMLDivElement {
  let el = pool.pop();
  if (!el) {
    el = document.createElement("div");
  }
  el.className = kind === "miss" ? "dmgfloat dmgfloat-miss" : "dmgfloat";
  el.style.opacity = "1";
  el.style.display = "block";
  return el;
}

function pushFloat(el: HTMLDivElement, wx: number, wy: number, wz: number): void {
  if (!container) return;
  container.appendChild(el);
  side = -side; // left, right, left, …
  floats.push({
    el,
    wx, wy, wz,
    age: 0,
    jx: side * SIDE,
  });
}

/** Spawn a red damage number at a world anchor (typically head-top).
 *  `amount` is the HP lost this tick — rounded for display. */
export function spawnDmgFloat(wx: number, wy: number, wz: number, amount: number): void {
  if (!container) return;
  const n = Math.max(1, Math.round(amount));
  if (!(n > 0) || !Number.isFinite(n)) return;

  const el = takeEl("hit");
  el.textContent = String(n);
  pushFloat(el, wx, wy, wz);
}

/** Spawn a muted "Miss" float at a world anchor (intended target head/chest).
 *  Fired on projectile arrival when the server rolled a band miss — same
 *  near-audience as the arrow itself. */
export function spawnMissFloat(wx: number, wy: number, wz: number): void {
  if (!container) return;
  const el = takeEl("miss");
  el.textContent = "Miss";
  pushFloat(el, wx, wy, wz);
}

/** Per-frame: age, project, rise, fade, recycle. Call from updateCombat. */
export function updateDmgFloats(camera: THREE.Camera, dt: number): void {
  if (!container || floats.length === 0) return;
  const W = window.innerWidth, H = window.innerHeight;
  for (let i = floats.length - 1; i >= 0; i--) {
    const f = floats[i]!;
    f.age += dt;
    if (f.age >= LIFE) {
      recycle(i);
      continue;
    }
    const t = f.age / LIFE; // 0 → 1
    // Ease-out rise; quick fade-in then slow fade-out.
    const rise = RISE * (1 - (1 - t) * (1 - t));
    const opacity = t < 0.12 ? t / 0.12 : 1 - (t - 0.12) / 0.88;

    _v.set(f.wx, f.wy, f.wz).project(camera);
    if (_v.z > 1) {
      // Behind the camera — keep alive (it may re-enter) but don't paint.
      if (f.el.style.display !== "none") f.el.style.display = "none";
      continue;
    }
    const l = Math.round((_v.x * 0.5 + 0.5) * W + f.jx);
    const top = Math.round((-_v.y * 0.5 + 0.5) * H - rise);
    f.el.style.left = `${l}px`;
    f.el.style.top = `${top}px`;
    f.el.style.opacity = String(Math.max(0, Math.min(1, opacity)));
    if (f.el.style.display !== "block") f.el.style.display = "block";
  }
}

/** Drop every live float (map swap / socket reset). */
export function clearDmgFloats(): void {
  for (let i = floats.length - 1; i >= 0; i--) recycle(i);
}

function recycle(i: number): void {
  const f = floats[i]!;
  f.el.style.display = "none";
  f.el.remove();
  pool.push(f.el);
  floats.splice(i, 1);
}
